<template>
    <div class="mb-8 bg-white shadow-md dark:bg-gray-800 rounded-lg">
        <div
            class="cursor-pointer flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-600 text-right border border-b-0 dark:border-gray-600 border-gray-200 sm:px-6 rounded-tl-md rounded-tr-md"
            :class="{'rounded-md' : !expanded}"
            @click="toggle"
        >
            <h4 class="text-lg font-semibold text-gray-600 dark:text-gray-300">
                {{ ratingsTitle }}
                <font-awesome-icon class="ml-2 text-red-600 dark:text-red-400" v-if="errors" :icon="['fas', 'exclamation-triangle']"></font-awesome-icon>
            </h4>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 9l-7 7-7-7"/>
            </svg>
        </div>
        <div
            class="px-6 pb-6 sm:rounded-bl-md sm:rounded-br-md border dark:border-gray-600 border-gray-200"
            v-show="expanded"
        >
            <div class="divide-y divide-dark">
                <div class="custom-field py-6"
                     v-for="(scoreRating, scoreRatingIndex) in scoreRatings"
                     :key="'scoreRating-' + scoreRatingIndex"
                >
                    <div class="flex flex-wrap">
                        <div class="w-1/4 px-3">
                            <div class="mb-3 text-sm">
                                        <span
                                            class="text-gray-700 dark:text-gray-400 text-sm mb-1">Minimum percentage*</span>
                                <div class="flex relative">
                                    <div
                                        class="absolute mt-1 inset-y-0 left-0 px-3 flex items-center justify-center text-sm font-medium leading-5 text-gray-700 border-r border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-l-md">
                                        %
                                    </div>
                                    <jet-input v-model="scoreRating.min_percent"
                                           class="pl-10 mt-1 rounded-r-md rounded-l-none"
                                           type="number" min="0" step="1" />
                                </div>
                                <jet-input-error :message="errors?.[scoreRatingIndex]?.min_percent"></jet-input-error>
                            </div>
                        </div>
                        <div class="w-1/4 px-3">
                            <div class="mb-3 text-sm">
                                <span class="text-gray-700 dark:text-gray-400">Color*</span>
                                <div class="flex flex-wrap items-center text-sm">
                                    <div class="flex-1">
                                        <jet-input :value="scoreRating.bgColor || '#000000'" type="color"
                                                   @input.prevent.stop="scoreRating.bgColor = $event.target.value"
                                                   class=""/>
                                    </div>
                                    <div class="w-1/2 ml-2">
                                        <jet-input v-model="scoreRating.bgColor"
                                                   type="text" />
                                    </div>
                                    <jet-button
                                        @click.prevent="scoreRating.bgColor = null"
                                        class="ml-2 px-2"
                                        :bg-dark="true"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                             fill="none" viewBox="0 0 24 24" stroke="white">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                        </svg>
                                    </jet-button>
                                </div>
                                <jet-input-error :message="errors?.[scoreRatingIndex]?.bgColor"></jet-input-error>
                            </div>
                        </div>
                        <div class="w-2/4 px-3">
                            <div class="mb-3 text-sm">
                                                <span
                                                    class="text-gray-700 dark:text-gray-400 text-sm mb-1">Rating label*</span>
                                <div class="flex relative">
                                    <jet-input v-model="scoreRating.label"
                                           type="text" />
                                </div>
                                <jet-input-error :message="errors?.[scoreRatingIndex]?.label"></jet-input-error>
                            </div>
                        </div>
                        <div class="w-full px-3">
                            <div class="mb-3 text-sm">
                                        <span
                                            class="text-gray-700 dark:text-gray-400 text-sm mb-1">Rating description*</span>
                                <ckeditor :editor="editor"
                                          v-model="scoreRating.description"
                                          class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-textarea focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                                          :config="editorConfig"></ckeditor>
                                <jet-input-error :message="errors?.[scoreRatingIndex]?.description"></jet-input-error>
                            </div>
                        </div>
                    </div>
                    <div class="px-3">
                        <jet-button
                            v-if="scoreRatings.length > 1"
                            @click.prevent="removeScoreRating(scoreRatingIndex)"
                            :bg-dark="true"
                        >
                            Delete Rating
                            <svg xmlns="http://www.w3.org/2000/svg" class="ml-2 h-4 w-4" fill="none"
                                 viewBox="0 0 24 24" stroke="white">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                      stroke-width="2"
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                        </jet-button>
                    </div>
                </div>

                <div class="pt-6">
                    <jet-button @click.prevent="addScoreRating"
                                :has-icon="true"
                    >
                        New Rating
                    </jet-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {useModelWrapper} from "@/composables/modelWrapper";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";
import JetInput from "@/Jetstream/Input";
import JetButton from "@/Jetstream/Button";
import JetInputError from "@/Jetstream/InputError";

export default {
    name: "SurveyTemplateScoreRatingEditor",
    components: {
        ckeditor: CKEditor.component,
        JetInputError,
        JetInput,
        JetButton
    },
    props: {
        expanded: {
            type: Boolean,
            default: false,
        },
        ratings: {
            type: Array,
            default: [],
        },
        errors: {
            type: Object,
        },
        ratingsTitle: {
            type: String,
            default: 'Score Ratings'
        }
    },
    emits: ['toggle', 'update:ratings'],
    setup(props, {emit}) {
        const toggle = () => {
            emit('toggle');
        }

        const scoreRatings = useModelWrapper(props, emit, 'ratings');

        const newScoreRating = () => {
            return {
                min_percent: 0,
                bgColor: '#000000',
                textColor: '#ffffff',
                label: 'New rating',
                description: '',
            };
        }

        const removeScoreRating = (scoreRatingIndex) => {
            scoreRatings.value.splice(scoreRatingIndex, 1);
        }

        const addScoreRating = () => {
            scoreRatings.value.push(newScoreRating());
        }

        const editor = ClassicEditor;

        const editorConfig = {
            removePlugins: ['imageUpload', 'mediaEmbed'],
            link: {
                addTargetToExternalLinks: true,
                defaultProtocol: 'http://',
            },
            toolbar: {
                removeItems: ['uploadImage', 'mediaEmbed']
            },
        }

        return {
            toggle,
            scoreRatings,
            removeScoreRating,
            addScoreRating,
            editor,
            editorConfig,
        }
    },
}
</script>

<style scoped>

</style>
